import { Component, ElementRef, ViewChild, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { DialogAddSiteComponent } from '@app/old-ui/dialog/dialog-add-site/dialog-add-site.component';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { User$, WebClient } from '@injectables';
import { vehiclesDefaultColors } from "src/colors";
import { Observable, combineLatest } from 'rxjs';
import { geojson } from '@lib/geojson'
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { DialogSetDeviceLocationComponent } from '@app/old-ui/dialog/dialog-set-device-location/dialog-set-device-location.component';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { Device, VehicleLocationQuery, Vehicle, User } from '@models';
import { Color } from '@lib/color';
import { op } from '@lib/rxjs';
import { MatDrawer } from '@angular/material/sidenav';
import { LocationMapService } from './location-map.service';
import { PolylineType } from '@app/utilities/map/features/polyline';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { DateFormatConfig } from '@app/utilities/date-format-config';

@Component({
    selector: 'location',
    templateUrl: './location.component.html',
    styleUrls: ['./location.component.scss']
})
export class LocationComponent implements OnInit, AfterViewInit, OnDestroy {  // Implement OnDestroy
    constructor(
        private readonly vehicleQuery: VehicleQueryService,
        public readonly user$: User$,
        private readonly dialog: DialogService,
        private readonly webClient: WebClient,
        private readonly user: User,
        public readonly fragQuery: FragQueryService,
        public readonly locationMapService: LocationMapService,
        public readonly dateFormat: DateFormatConfig
    ) { }

    @ViewChild('map', { static: false }) mapElement: ElementRef;

    private readonly addSiteDialog = DialogAddSiteComponent;
    private readonly setDevceLocation = DialogSetDeviceLocationComponent;
    public readonly device$ = this.vehicleQuery.devices$;
    public readonly devices$ = this.vehicleQuery.devices$;
    public readonly sites$ = this.vehicleQuery.sites$;
    public readonly duration$ = this.vehicleQuery.duration$;
    public readonly durationOptions$ = this.vehicleQuery.durationOptions;
    public readonly geofenceRecords$ = this.vehicleQuery.geofenceRecords$;
    public readonly dateTimeFormat = this.dateFormat.getFormat()
    public drawingManagerOption: DrawingManagerOption;
    public disableAddDevice: Boolean = true;
    public unassignedDevices: Device[] = [];
    public VehiclesPathLocations: VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[];
    private readonly colors = vehiclesDefaultColors;
    public vehicleProperties: [Vehicle, Color, boolean][] = [];
    public readonly deviceClient = this.webClient.model.device;
    public vehicleListDrawerOpen = false;
    public geofenceRecordDrawerOpen = false;
    private mapInitialized = false; // Track map initialization
    private vehicleTracePaths: PolylineType[] = [];
	public readonly trigger$ = this.fragQuery.trigger$;

    @ViewChild('vehicles') vehiclesList: MatSelectionList;
    @ViewChild('drawer') drawer: MatDrawer;
    @ViewChild('geofenceDrawer') geofenceDrawer: MatDrawer;
    
    ngOnInit() {
        this.locationMapService.vehicleListDrawerToggle$.subscribe(() => {
            this.toggleDrawers('vehiclelist');
        });

        this.locationMapService.geofenceRecordDrawerToggle$.subscribe(() => {
            this.toggleDrawers('geofenceRecord');
        });

        this.devices$.subscribe(devices => {
            devices.forEach(device => {
                if (!device.geometry) {
                    this.unassignedDevices.push(device)
                    this.disableAddDevice = false;
                }
            })
        })

        this.vehicleQuery.vehicles$.subscribe(vehicles => {
            const selectedStateByVehicleId = new Map(
                this.vehicleProperties.map(([vehicle, , isSelected]) => [vehicle.id, isSelected])
            );

            this.vehicleProperties = (vehicles ?? []).map((vehicle, index) => [
                vehicle,
                this.colors[index % this.colors.length],
                selectedStateByVehicleId.get(vehicle.id) ?? true,
            ]);
            this.locationMapService.vehicleProperties$.next(this.vehicleProperties);
        });

        this.vehicleLocations$.subscribe(locations => {
            if (!locations) {
                return;
            }

            this.VehiclesPathLocations = locations;
            this.updatePath();
        })
    }

    public toggleDrawers(drawerType: 'vehiclelist' | 'geofenceRecord') {
        if (drawerType === 'vehiclelist') {
            if (this.geofenceDrawer.opened) {
                this.geofenceDrawer.close().then(() => {
                    this.drawer.toggle();
                    this.vehicleListDrawerOpen = this.drawer.opened;
                });
            } else {
                this.drawer.toggle();
                this.vehicleListDrawerOpen = this.drawer.opened;
            }
            this.geofenceRecordDrawerOpen = false;
        } else if (drawerType === 'geofenceRecord') {
            if (this.drawer.opened) {
                this.drawer.close().then(() => {
                    this.geofenceDrawer.toggle();
                    this.geofenceRecordDrawerOpen = this.geofenceDrawer.opened;
                });
            } else {
                this.geofenceDrawer.toggle();
                this.geofenceRecordDrawerOpen = this.geofenceDrawer.opened;
            }
            this.vehicleListDrawerOpen = false;
        }
    }

    updatePath() {
        this.processVehiclesPathLocations();
        this.locationMapService.addTracePaths(this.vehicleTracePaths);
    }

    ngAfterViewInit() {
        if (!this.mapInitialized) {
            setTimeout(() => {
                this.locationMapService.initializeMap([0, 0], this.mapElement);
                this.mapInitialized = true;
            }, 100);
        }
    }

    ngOnDestroy() {
        this.locationMapService.cleanup();
        this.mapInitialized = false;
    }

    public toggleDrawer() {
        this.drawer.toggle();
    }

    public onVehicleSelectionChange(event: MatSelectionListChange) {
        const selectedVehicleIds = new Set(
            event.source.selectedOptions.selected.map((option) => option.value?.id)
        );

        this.vehicleProperties = this.vehicleProperties.map(([vehicle, color]) => [
            vehicle,
            color,
            selectedVehicleIds.has(vehicle.id),
        ]);

        this.locationMapService.vehicleProperties$.next(this.vehicleProperties);
        this.updatePath();
    }

    public async showDrawingManager(type: DrawingManagerOption) {
        this.toggleDrawer();
        this.vehicleListDrawerOpen = false
        const polygonCoords = await this.locationMapService.draw(type);
        if (type === 'Polygon') {
            this.addnewPolygon(polygonCoords as geojson.GeometryPolygon);
        } else if (type === 'Point') {
            this.addDeviceLocation(polygonCoords as geojson.GeometryPoint)
        }
    }

    public async addnewPolygon(newPolygon: geojson.GeometryPolygon) {
        await this.dialog.open(this.addSiteDialog, newPolygon);
        this.trigger$.next()
    }

    public async addDeviceLocation(newPoint: geojson.GeometryPoint) {
        await this.dialog.open(this.setDevceLocation, { point: newPoint, unassignedDevices: this.unassignedDevices });
    }

    public readonly vehicleLocations$: Observable<VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] | null> = combineLatest(
        [
            this.vehicleQuery.vehicleLocationForInterval$,
            this.vehicleQuery.vehicles$
        ]
    ).pipe(
        op.map(([response, vehicles]) => response
            ? getVehiclecolor(response, this.vehicleProperties, vehicles)
            : null
        )
    )

    private processVehiclesPathLocations(): void {
        this.vehicleTracePaths = [];
        const selectedVehicleIds = new Set(
            (this.vehicleProperties ?? [])
                .filter(([, , isSelected]) => isSelected)
                .map(([vehicle]) => vehicle.id)
        );
        const hasSelectionState = (this.vehicleProperties ?? []).length > 0;

        (this.VehiclesPathLocations ?? [])
            .filter((vehicleLocations) => !hasSelectionState || selectedVehicleIds.has(vehicleLocations.vehicleId))
            .forEach((vehicleLocations) => {
                this.vehicleTracePaths.push({
                    content: '',
                    coordinates: vehicleLocations.vehicleLocations,
                    color: vehicleLocations.color.toString(),
                });
            });
    }
}

export type DrawingManagerOption = 'Polygon' | 'Point';

const getVehiclecolor = (vehicleLocationsResponse: VehicleLocationQuery.Interval.Response, vehicleProperties: [Vehicle, Color, boolean][], vehicles: Vehicle[]): VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] => {
    if (vehicleLocationsResponse) {
        const vehicleTracePaths = vehicleLocationsResponse.tracePaths.map(vehicleLocations => {
            const vehicleColor = vehicleProperties.find(vehicle => vehicle[0].id === vehicleLocations.vehicleId)?.[1]
                ?? vehiclesDefaultColors[0];
            return {
                vehicleName: vehicleLocations.vehicleName,
                vehicleId: vehicleLocations.vehicleId,
                color: vehicleColor,
                vehicleLocations: vehicleLocations.vehicleLocations
            }
        });
        return vehicleTracePaths;
    }
    return [];
}
